<template>
  <div v-if="actualActiveFilterCount > 0" class="mb-4">
    <div class="flex items-center justify-between mb-2">
      <div class="flex items-center gap-2">
        <h3 class="text-sm font-medium">Selected Filters</h3>
        <span class="text-sm text-muted-foreground">({{ actualActiveFilterCount }} active)</span>
      </div>
      <button 
        @click="clearAllFilters"
        class="text-sm text-muted-foreground hover:text-destructive transition-colors"
        aria-label="Reset all filters"
      >
        Reset all filters
      </button>
    </div>
    <div class="relative">
      <div class="overflow-x-auto scrollbar-none -mx-4 px-4 pb-1">
        <div class="flex gap-2 flex-nowrap">
          <!-- Loop through all active filters -->
          <!-- When favorites is active, only favorites will be displayed -->
          <template v-for="(filterValue, filterName) in activeFilters" :key="filterName">
            <div 
              v-if="shouldDisplayFilter(filterName, filterValue)"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted text-sm rounded-md hover:bg-muted/80 transition-colors group shrink-0"
            >
              <span>{{ getDisplayName(filterName, filterValue) }}</span>
              <span class="text-muted-foreground">{{ getFilterCount(filterName, filterValue) }}</span>
              <button 
                @click="clearFilter(filterName)"
                class="text-muted-foreground/50 hover:text-destructive transition-colors ml-1"
                aria-label="Remove filter"
              >
                <XMarkIcon class="h-3.5 w-3.5" />
              </button>
            </div>
          </template>
        </div>
      </div>
      <div class="absolute right-0 top-0 bottom-0 w-8 bg-linear-to-l from-background to-transparent pointer-events-none"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useVehiclesStore } from '~/stores/vehicles'
import { useFilters } from '~/layers/base/composables/useFilters'
import { computed, onMounted, watch } from 'vue'

const props = defineProps<{
  activeFilters: Record<string, any>;
  activeFilterCount: number;
}>()

const emit = defineEmits<{
  'clear-filter': [filterName: string];
  'clear-all-filters': [];
}>()

const { 
  getFilter, 
  getFilterDisplayName, 
  shouldDisplayFilter, 
  countActiveFilters,
  isInitializing 
} = useFilters()

// Computed property to get the actual number of active filters that should be displayed
const actualActiveFilterCount = computed(() => {
  if (isInitializing.value) return 0;
  const count = countActiveFilters(props.activeFilters);
  return count;
});

// Add mounted lifecycle hook to log the active filters
onMounted(() => {
  // console.log('[ActiveFilters] Component mounted:', {
  //   activeFilters: props.activeFilters,
  //   count: props.activeFilterCount,
  //   isInitializing: isInitializing.value
  // });
  
  // Log each filter and whether it should be displayed
  Object.entries(props.activeFilters).forEach(([filterName, filterValue]) => {
    // console.log(`[ActiveFilters] Filter ${filterName}:`, {
    //   value: filterValue,
    //   shouldDisplay: shouldDisplayFilter(filterName, filterValue),
    //   displayName: getDisplayName(filterName, filterValue),
    //   count: getFilterCount(filterName, filterValue)
    // });
  });
});

// Watch for changes in active filters and log them
watch(() => props.activeFilters, (newFilters) => {
  // console.log('[ActiveFilters] Active filters changed:', {
  //   filters: newFilters,
  //   count: countActiveFilters(newFilters)
  // });
}, { deep: true });

// Get the count display for a filter (e.g., "3 selected")
const getFilterCount = (filterName: string, value: any): string => {
  if (isInitializing.value) return '';
  
  // Handle direct array values
  if (Array.isArray(value)) {
    return `${value.length}`;
  }
  
  // Handle structured array format {type: 'array', value: [...]}
  if (value && typeof value === 'object' && value.type === 'array' && Array.isArray(value.value)) {
    return `${value.value.length}`;
  }
  
  return '';
}

// Get display name for a filter
const getDisplayName = (filterName: string, value: any): string => {
  if (isInitializing.value) return filterName;
  
  // If we have a structured filter value with type and value properties
  if (value && typeof value === 'object' && value.type) {
    if (value.type === 'array' && Array.isArray(value.value)) {
      // For array-type filters, let the useFilters utility handle formatting
      return getFilterDisplayName(filterName, value.value);
    }
    
    // For other structured types, pass the value property
    if ('value' in value) {
      return getFilterDisplayName(filterName, value.value);
    }
  }
  
  // Default behavior - use the utility function
  return getFilterDisplayName(filterName, value);
}

const clearFilter = (filterName: string) => {
  emit('clear-filter', filterName)
}

const clearAllFilters = () => {
  emit('clear-all-filters')
}
</script>
